import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import GiveawayOfferPage from './GiveawayOfferPage';
import GiveawayOfferConfirmationPage from './GiveawayOfferConfirmationPage';
import GiveawayOfferSubscribedPage from './GiveawayOfferSubscribedPage';
import { PAGE_BG } from './giveawayOfferTheme';
import {
  loadOfferSlugs,
  loadedOfferSlugs,
  offerPageFor,
  offerPageShape,
  storedOfferSlugs,
  type OfferPage
} from './giveawayOfferPages';

/* The poster funnel on an admin-chosen path — `/token2049-offer` and its
 * `/confirmation` and `/subscribed` — mounted at `/:offerSlug/*` in App.tsx.
 *
 * It renders one of the three funnel pages when the first segment is a slug the
 * API lists, and `fallback` otherwise. App.tsx hands it exactly what the `*`
 * catch-all renders, and the route only ever sees URLs nothing else in the table
 * claims (see the note there), so an unknown path looks as it always did.
 *
 * WHILE IT ASKS, IT SHOWS THE FUNNEL'S EMPTY DARK PAGE AND NOTHING ELSE — not
 * the fallback, because a poster scan must not flash the homepage before the
 * offer. A path that cannot be an offer page at all (a malformed slug, or
 * anything after it but `confirmation` or `subscribed`) never waits: it falls
 * straight through.
 */

const PAGES: Record<OfferPage, ComponentType> = {
  offer: GiveawayOfferPage,
  confirmation: GiveawayOfferConfirmationPage,
  subscribed: GiveawayOfferSubscribedPage
};

const GiveawayOfferGate = ({ fallback }: { fallback: ReactNode }) => {
  const { offerSlug, '*': rest } = useParams();
  const [answer, setAnswer] = useState<readonly string[] | null>(null);

  /* This page load's answer when there is one; until then the copy a reload
     left behind, which may say yes but is never trusted to say no. */
  const loaded = answer ?? loadedOfferSlugs();
  const page = offerPageFor(offerSlug, rest, loaded ?? storedOfferSlugs() ?? []);
  const asking = !page && !loaded && offerPageShape(offerSlug, rest) !== null;

  useEffect(() => {
    if (!asking) return undefined;
    let cancelled = false;
    loadOfferSlugs().then((slugs) => {
      if (!cancelled) setAnswer(slugs);
    });
    return () => {
      cancelled = true;
    };
  }, [asking]);

  if (page) {
    const Page = PAGES[page];
    /* Keyed on the slug, so back/forward between two offers is a new page and
       its view is counted against the right one. */
    return <Page key={(offerSlug ?? '').toLowerCase()} />;
  }

  if (asking) {
    return <Box sx={{ background: PAGE_BG, minHeight: '100vh' }} />;
  }

  return <>{fallback}</>;
};

export default GiveawayOfferGate;
